import { createFileRoute } from "@tanstack/react-router"
import { Container, Title, Text, Paper, Stack, Grid, Box, List, Badge, Accordion, Table } from "@mantine/core"
import { IconShieldCheck, IconAlertTriangle } from "@tabler/icons-react"

export const Route = createFileRoute("/methodology")({
  component: MethodologyComponent,
})

function MethodologyComponent() {
  const sourceTiers = [
    {
      tier: 1,
      title: "Primary Sources",
      color: "green",
      description: "Highest Credibility",
      examples: [
        "Official court documents and filings",
        "Government agency records (FBI, DOJ)",
        "Sworn depositions and testimony",
        "Official police reports",
        "Legislative hearing transcripts",
      ],
    },
    {
      tier: 2,
      title: "Credible Secondary Sources",
      color: "blue",
      description: "High Credibility",
      examples: [
        "Established news organizations with fact-checking standards",
        "Investigative journalism with documented sources",
        "Academic research and analysis",
        "Legal expert commentary",
        "Official statements from verified parties",
      ],
    },
    {
      tier: 3,
      title: "Supporting Sources",
      color: "yellow",
      description: "Use with Caution",
      examples: [
        "Social media posts from verified accounts",
        "Books by credible authors with documented sources",
        "Documentary films with verified information",
        "Leaked documents (requiring extra verification)",
      ],
    },
    {
      tier: 4,
      title: "Unreliable Sources",
      color: "red",
      description: "Avoid",
      examples: [
        "Anonymous claims without verification",
        "Conspiracy theory websites",
        "Social media rumors",
        "Unverified leaks",
        "Sensationalized media content",
      ],
    },
  ]

  const verificationPhases = [
    {
      phase: 1,
      title: "Initial Assessment",
      description: "Evaluate relevance, legal compliance, and priority",
      details: [
        "Case relevance check",
        "Public interest evaluation",
        "Legal and ethical risk screening",
        "Priority classification (High/Medium/Low)",
      ],
    },
    {
      phase: 2,
      title: "Source Evaluation",
      description: "Assess source credibility and authenticity",
      details: [
        "Apply 4-tier source credibility hierarchy",
        "Document source credentials",
        "Check previous accuracy record",
        "Identify potential conflicts of interest",
      ],
    },
    {
      phase: 3,
      title: "Claim Verification",
      description: "Independently verify all factual claims",
      details: [
        "Extract all factual claims",
        "Check against primary sources (PACER, official records)",
        "Require multiple independent sources",
        "Verify quotes against original transcripts",
      ],
    },
    {
      phase: 4,
      title: "Cross-Reference Validation",
      description: "Confirm consistency across multiple sources",
      details: [
        "Compare information across 2-4 sources (depending on tier)",
        "Check for discrepancies in dates, names, events",
        "Investigate contradictions",
        "Verify timeline accuracy",
      ],
    },
    {
      phase: 5,
      title: "Legal & Ethical Review",
      description: "Ensure compliance and protect privacy",
      details: [
        "Legal compliance check",
        "Privacy protection review",
        "Victim sensitivity assessment",
        "Redaction of sensitive information",
      ],
    },
    {
      phase: 6,
      title: "Documentation",
      description: "Record verification process and findings",
      details: [
        "Complete source citations",
        "Verification status assignment",
        "Confidence level determination",
        "Last verified date recorded",
      ],
    },
    {
      phase: 7,
      title: "Publication Decision",
      description: "Final review before publishing",
      details: [
        "Quality control checkpoint",
        "Peer review for high-priority items",
        "Verification badge assignment",
        "Corrections policy implementation",
      ],
    },
  ]

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Box ta="center" mb="xl">
          <Box
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
              borderRadius: "16px",
              padding: "12px",
              boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)",
              display: "inline-flex",
              marginBottom: "24px",
            }}
          >
            <IconShieldCheck size={32} color="white" />
          </Box>
          <Title order={1} size="h1" mb="md">
            Verification Methodology
          </Title>
          <Text size="xl" c="dimmed" maw={700} mx="auto">
            How we ensure accuracy, credibility, and transparency in all published information
          </Text>
        </Box>

        {/* Overview */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h2" mb="md">
              Why Methodology Matters
            </Title>
            <Text size="lg" lh={1.6}>
              Transparency advocacy requires more than passion—it requires bulletproof accuracy. Every document, news
              article, and timeline event on RTEFN is verified using rigorous standards aligned with the International
              Fact-Checking Network (IFCN) Code of Principles.
            </Text>
            <Text size="lg" lh={1.6}>
              This page explains exactly how we verify information, assess source credibility, and ensure legal and
              ethical compliance. Our methodology is open for scrutiny because transparency demands transparency.
            </Text>
          </Stack>
        </Paper>

        {/* Core Principles */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              Core Principles
            </Title>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Paper shadow="xs" radius="md" p="md" h="100%">
                  <Stack gap="sm">
                    <Badge color="blue" variant="light" size="lg">
                      IFCN-Aligned
                    </Badge>
                    <Title order={4} size="h5">
                      Nonpartisanship
                    </Title>
                    <Text size="sm" c="dimmed">
                      Apply the same verification standard regardless of source or political implications
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Paper shadow="xs" radius="md" p="md" h="100%">
                  <Stack gap="sm">
                    <Badge color="blue" variant="light" size="lg">
                      Complete Transparency
                    </Badge>
                    <Title order={4} size="h5">
                      Open Methodology
                    </Title>
                    <Text size="sm" c="dimmed">
                      Document and explain all verification processes publicly
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Paper shadow="xs" radius="md" p="md" h="100%">
                  <Stack gap="sm">
                    <Badge color="blue" variant="light" size="lg">
                      Multiple Sources
                    </Badge>
                    <Title order={4} size="h5">
                      Independent Verification
                    </Title>
                    <Text size="sm" c="dimmed">
                      Require 2-4 independent sources depending on claim significance
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Paper shadow="xs" radius="md" p="md" h="100%">
                  <Stack gap="sm">
                    <Badge color="blue" variant="light" size="lg">
                      Open Corrections
                    </Badge>
                    <Title order={4} size="h5">
                      Error Transparency
                    </Title>
                    <Text size="sm" c="dimmed">
                      Publish clear corrections promptly when errors are identified
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Stack>
        </Paper>

        {/* Source Credibility Hierarchy */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              Source Credibility Hierarchy
            </Title>
            <Text size="md" lh={1.6} mb="md">
              All sources are evaluated using a 4-tier credibility system. Content marked as "verified" requires
              confirmation from Tier 1 or Tier 2 sources with proper documentation.
            </Text>
            <Stack gap="md">
              {sourceTiers.map((tier) => (
                <Paper key={tier.tier} shadow="xs" radius="md" p="lg" withBorder>
                  <Stack gap="sm">
                    <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Badge color={tier.color} variant="filled" size="lg">
                        TIER {tier.tier}
                      </Badge>
                      <Title order={4} size="h5" style={{ flex: 1 }}>
                        {tier.title}
                      </Title>
                      <Badge color={tier.color} variant="light" size="sm">
                        {tier.description}
                      </Badge>
                    </Box>
                    <List size="sm" mt="xs">
                      {tier.examples.map((example, idx) => (
                        <List.Item key={idx}>{example}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Stack>
        </Paper>

        {/* Verification Process */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              7-Phase Verification Process
            </Title>
            <Text size="md" lh={1.6} mb="md">
              Every piece of content follows a systematic verification workflow with clear stop/go checkpoints at each
              phase.
            </Text>
            <Accordion variant="separated">
              {verificationPhases.map((phase) => (
                <Accordion.Item key={phase.phase} value={`phase-${phase.phase}`}>
                  <Accordion.Control>
                    <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Badge color="blue" variant="light" size="lg">
                        PHASE {phase.phase}
                      </Badge>
                      <div>
                        <Title order={5} size="h6">
                          {phase.title}
                        </Title>
                        <Text size="sm" c="dimmed">
                          {phase.description}
                        </Text>
                      </div>
                    </Box>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <List size="sm">
                      {phase.details.map((detail, idx) => (
                        <List.Item key={idx}>{detail}</List.Item>
                      ))}
                    </List>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Stack>
        </Paper>

        {/* Verification Status Levels */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              Verification Status Levels
            </Title>
            <Text size="md" lh={1.6} mb="md">
              Content is clearly marked with one of these verification statuses:
            </Text>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Criteria</Table.Th>
                  <Table.Th>Source Requirements</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td>
                    <Badge color="green" variant="light">
                      VERIFIED
                    </Badge>
                  </Table.Td>
                  <Table.Td>Confirmed through multiple credible sources</Table.Td>
                  <Table.Td>2+ Tier 1-2 sources with cross-verification</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Badge color="blue" variant="light">
                      PARTIALLY VERIFIED
                    </Badge>
                  </Table.Td>
                  <Table.Td>Some claims confirmed, others pending</Table.Td>
                  <Table.Td>At least 1 Tier 1-2 source for confirmed claims</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Badge color="gray" variant="light">
                      UNVERIFIED
                    </Badge>
                  </Table.Td>
                  <Table.Td>Cannot independently confirm through available sources</Table.Td>
                  <Table.Td>Single source or Tier 3-4 sources only</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Badge color="yellow" variant="light">
                      DISPUTED
                    </Badge>
                  </Table.Td>
                  <Table.Td>Contradictory information from credible sources</Table.Td>
                  <Table.Td>Conflicting Tier 1-2 sources</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Badge color="red" variant="light">
                      FALSE
                    </Badge>
                  </Table.Td>
                  <Table.Td>Proven false through authoritative sources</Table.Td>
                  <Table.Td>Tier 1 sources directly contradict claim</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Stack>
        </Paper>

        {/* Document Verification */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              How We Verify Court Documents
            </Title>
            <List spacing="md" size="md">
              <List.Item>
                <strong>Authentication:</strong> Verify document origin through official court databases (PACER,
                CourtListener)
              </List.Item>
              <List.Item>
                <strong>Case Number Verification:</strong> Cross-reference case numbers, filing dates, and court
                jurisdictions
              </List.Item>
              <List.Item>
                <strong>Content Analysis:</strong> Extract key facts and verify claims against court records
              </List.Item>
              <List.Item>
                <strong>Chain of Custody:</strong> Document where and when the document was obtained
              </List.Item>
              <List.Item>
                <strong>Archival:</strong> Store verified copies in Cloudflare R2 to prevent broken links and ensure
                long-term availability
              </List.Item>
            </List>
          </Stack>
        </Paper>

        {/* News Verification */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              How We Verify News Articles
            </Title>
            <List spacing="md" size="md">
              <List.Item>
                <strong>Source Credibility Assessment:</strong> Evaluate news organization using tier system
              </List.Item>
              <List.Item>
                <strong>Claim Extraction:</strong> Identify all factual claims separate from opinion
              </List.Item>
              <List.Item>
                <strong>Independent Verification:</strong> Check each claim against primary sources (court records,
                official statements)
              </List.Item>
              <List.Item>
                <strong>Cross-Reference Validation:</strong> Confirm information with 2-4 independent sources depending
                on significance
              </List.Item>
              <List.Item>
                <strong>Transparency Flagging:</strong> Identify government-influenced content, partial releases, and
                manipulation patterns
              </List.Item>
            </List>
          </Stack>
        </Paper>

        {/* Red Flags */}
        <Paper shadow="sm" radius="md" p="xl" mb="xl" style={{ borderLeft: "4px solid #fa5252" }}>
          <Stack gap="lg">
            <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <IconAlertTriangle size={24} color="#fa5252" />
              <Title order={2} size="h3">
                Content Requiring Immediate Verification
              </Title>
            </Box>
            <Text size="md" lh={1.6}>
              The following types of content undergo enhanced verification before publication:
            </Text>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <List size="sm">
                  <List.Item>Specific criminal allegations</List.Item>
                  <List.Item>Financial transaction details</List.Item>
                  <List.Item>Dates of significant events</List.Item>
                  <List.Item>Quotes attributed to individuals</List.Item>
                </List>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <List size="sm">
                  <List.Item>Legal outcomes and court decisions</List.Item>
                  <List.Item>Information about minors</List.Item>
                  <List.Item>Unverified accusations</List.Item>
                  <List.Item>Government-released partial documents</List.Item>
                </List>
              </Grid.Col>
            </Grid>
          </Stack>
        </Paper>

        {/* Corrections Policy */}
        <Paper shadow="sm" radius="md" p="xl">
          <Stack gap="lg">
            <Title order={2} size="h3" mb="md">
              Corrections Policy
            </Title>
            <Text size="md" lh={1.6}>
              We are committed to accuracy and transparency in correcting errors:
            </Text>
            <List spacing="md" size="md" mt="md">
              <List.Item>
                <strong>Immediate Action:</strong> Errors are corrected as soon as they are identified
              </List.Item>
              <List.Item>
                <strong>Prominent Display:</strong> Corrections are clearly marked and explained
              </List.Item>
              <List.Item>
                <strong>No Silent Edits:</strong> All corrections include an explanation of what was changed and why
              </List.Item>
              <List.Item>
                <strong>Updated Verification:</strong> Corrected content is re-verified and verification date is updated
              </List.Item>
              <List.Item>
                <strong>Transparency:</strong> Significant corrections are announced in news updates
              </List.Item>
            </List>
          </Stack>
        </Paper>

        {/* Footer */}
        <Box ta="center" mt="xl" pt="xl" style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}>
          <Badge variant="light" color="blue" size="lg" px="xl" py="sm">
            Methodology Version 1.0 • Last Updated: 2025
          </Badge>
        </Box>
      </Stack>
    </Container>
  )
}
